import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ModernTable from "../../../../components/ModernTable";
import { ColumnDef } from "@tanstack/react-table";
import { Roles, formatDate, leadStyes } from "../../../../util/constant";
import useCampaign from "./useCampaign";
import Modal from "../../../../components/Model";
import ApiService from "../../../../service/ApiService";
import { apiPaths } from "../../../../service/apiPaths";
import toast from "react-hot-toast";
import Button from "../../../../components/Button";
import { CalendarPlus, Edit, Upload, View } from "lucide-react";
import * as XLSX from "xlsx";

type CampaignRow = {
  _id: string;
  userId?: { _id: string; name: string; email: string };
  companyName?: string;
  email?: string;
  leadName?: string;
  linkedInLeadStatus?: string;
  personalization?: string;
  leadStatus?: string;
  services?: string;
  instagramLink?: string;
  companyWebsite?: string;
  leadWebLink?: string;
  linkedInLink?: string;
  description?: string;
  query?: string;
  follow1?: string;
  follow2?: string;
  createdAt?: string;
  updatedAt?: string;
  aiData?: string;
  action?: string;
};

const TruncatedText = ({
  text = "",
  maxWords = 10,
  className = "text-sm",
}: {
  text?: string;
  maxWords?: number;
  className?: string;
}) => {
  const str = (text ?? "").toString();
  const words = str.trim().length ? str.trim().split(/\s+/) : [];
  const needsTruncate = words.length > maxWords;
  const display = needsTruncate
    ? `${words.slice(0, maxWords).join(" ")}...`
    : str || "-";
  return (
    <span className={className} title={str}>
      {display || "-"}
    </span>
  );
};

const LEAD_STATUS_OPTIONS = [
  "New",
  "Send For Review",
  "Approved",
  "Skip",
  "Rejected",
  "Connected",
  "Converted",
  "Lost",
  "Duplicate",
  "Not Interested",
  "On Hold",
  "Reminder",
  "Follow-Up-1",
  "Follow-Up-2",
  "Scheduled",
  "Query",
  "Future Follow-up",
  "Email Issue"
];

const LINKEDIN_STATUS_OPTIONS = [
  "Connection send",
  "Accepted",
  "Convertion",
  "Interested",
  "Skip",
  "Won",
  "Future Follow-up",
  "Lost",
];

const Campaign = ({ campaignId }: { campaignId?: string }) => {
  const params = useParams();
  const effectiveCampaignId = campaignId || params.id;
  const {
    campaignData,
    search,
    loading,
    error,
    page,
    totalPage,
    totalItems,
    setPage,
    setSearch,
    leadStatusFilter,
    setLeadStatusFilter,
    fetchCampaignData,
    handleOpenCreate,
    handleCloseCreate,
    form,
    handleChange,
    handleCreate,
    isCreateOpen,
    isSubmitting,
    services,
    handleTableStatusChange,
    handleTableLinkedInStatusChange,
    handleTableServiceChange,
    isEditOpen,
    handleOpenEdit,
    handleCloseEdit,
    handleUpdate,
    isViewOpen,
    handleOpenView,
    handleCloseView,
    viewData,
  } = useCampaign(effectiveCampaignId);

  const role =
    typeof window !== "undefined" ? localStorage.getItem("role") : null;
  const isAdmin = role === Roles.admin;
  const navigate = useNavigate();
  const location = useLocation() as any;
  const [campaignName, setCampaignName] = useState<string>(
    (location?.state && location.state.campaignName) || ""
  );

  // Initialize filter from navigation state or query string
  useEffect(() => {
    try {
      const stateFilter = location?.state?.filterStatus as string | undefined;
      const searchParams = new URLSearchParams(location?.search || "");
      const qsFilter = searchParams.get("status") || undefined;
      const initial = stateFilter || qsFilter;
      if (initial) {
        setLeadStatusFilter(initial);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve campaign name from state or fetch by id as a fallback
  useEffect(() => {
    const hasName = (campaignName || "").trim().length > 0;
    const id = effectiveCampaignId;
    if (!hasName && id) {
      (async () => {
        try {
          const resp = await ApiService({
            method: "GET",
            endpoint: `${apiPaths.campaignGetAll}`,
          });
          const counts: any[] = Array.isArray(resp?.counts) ? resp.counts : [];
          const match = counts.find((c: any) => String(c?.campaignId || "") === String(id));
          if (match?.campaignName) setCampaignName(match.campaignName);
        } catch {}
      })();
    }
  }, [campaignName, effectiveCampaignId]);

  const columns = useMemo<ColumnDef<CampaignRow>[]>(() => {
    const baseCols: ColumnDef<CampaignRow>[] = [
      {
        accessorKey: "leadName",
        header: "Lead Name",
        size: 140,
        cell: ({ row }) => (
          <div className="flex flex-col gap-2">
            <TruncatedText text={row.original.leadName} className="text-sm" />
          </div>
        ),
      },
      {
        accessorKey: "companyName",
        header: "Company",
        size: 160,
        cell: ({ row }) => (
          <div className="flex flex-col gap-2">
            <TruncatedText
              text={row.original.companyName}
              className="text-sm"
            />
            {row.original.linkedInLink ? (
              <a
                className="text-xs text-blue-500 border-b w-fit border-blue-500"
                href={row.original.linkedInLink || "-"}
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            ) : (
              <a
                className="text-xs text-blue-500 border-b w-fit border-blue-500"
                href={row.original.companyWebsite || "-"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Website
              </a>
            )}
          </div>
        ),
      },
      {
        accessorKey: "leadStatus",
        header: "Status",
        size: 120,
        cell: ({ row }) => (
          <select
            className={`${
                leadStyes[row.original.leadStatus || "New"] || "bg-gray-200 "
              } min-w-20`}
            value={row.original.leadStatus || "New"}
            onChange={(e) =>
              handleTableStatusChange(row.original._id, e.target.value)
            }
          >
            <option value="">Select status</option>
            {LEAD_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        ),
      },
      {
        accessorKey: "linkedInLeadStatus",
        header: "LinkedIn Status",
        size: 120,
        cell: ({ row }) => (
          <select
            className="text-xs border rounded px-2 py-1 w-28"
            value={row.original.linkedInLeadStatus || ""}
            onChange={(e) =>
              handleTableLinkedInStatusChange(row.original._id, e.target.value)
            }
          >
            <option value="">Select status</option>
            {LINKEDIN_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        ),
      },
      {
        accessorKey: "services",
        header: "Service",
        size: 120,
        cell: ({ row }) => (
          <select
            className="text-xs border rounded px-2 py-1 text-black bg-blue-50 w-26"
            value={row.original.services || ""}
            onChange={(e) =>
              handleTableServiceChange(row.original._id, e.target.value)
            }
          >
            <option value="">Select service</option>
            {services?.map((s: any) => (
              <option key={s.id || s._id || s.type} value={s.id || s._id || ""}>
                {s.type || s.name}
              </option>
            ))}
          </select>
        ),
      },
      {
        accessorKey: "query",
        header: "Query",
        size: 120,
        cell: ({ row }) => (
          <TruncatedText text={row.original.query} className="text-xs" />
        ),
      },
      {
        accessorKey: "action",
        header: "Action",
        size: 60,
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              type="button"
              className={`text-left w-full  text-indigo-700`}
              title="Schedule Lead"
              onClick={() => {
                const currentSearch = window.location.search;
                const params = new URLSearchParams(currentSearch);
                const currentPage = params.get("page") || "1";
                navigate(`/campaign-schedule/${row.original._id}?page=${currentPage}`);
              }}
            >
              <CalendarPlus />
            </button>
            <button
              className="text-xs  py-1 text-blue-600 hover:text-blue-800"
              onClick={() => handleOpenEdit(row.original)}
              type="button"
              title="Query"
            >
              <Edit />
            </button>
          </div>
        ),
      },
    ];

    return baseCols;
  }, [isAdmin, services]);

  const [uploading, setUploading] = useState(false);
  const fileInputId = "campaign-csv-input";


  const handleUploadCsvClick = () => {
    const el = document.getElementById(fileInputId) as HTMLInputElement | null;
    el?.click();
  };

  const handleCsvSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      let uploadFile: File = file;

      const isExcelFile =
        /\.(xlsx|xls)$/i.test(file.name) ||
        file.type === "application/vnd.ms-excel" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      if (isExcelFile) {
        // Convert first sheet of Excel to CSV to reuse existing CSV import endpoint
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const csvText = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csvText], { type: "text/csv" });
        const csvName = `${
          file.name.replace(/\.(xlsx|xls)$/i, "") || "import"
        }.csv`;
        uploadFile = new File([blob], csvName, { type: "text/csv" });
      }

      const formData = new FormData();
      formData.append("csvFile", uploadFile);
      if (effectiveCampaignId) {
        formData.append("campaignId", effectiveCampaignId);
      }

      const resp = await ApiService({
        method: "POST",
        endpoint: apiPaths.campaignImportCsv,
        headers: { "Content-Type": "multipart/form-data" },
        data: formData,
      });
      const summary = resp?.summary || {};
      const total = summary?.totalRows ?? summary?.totalCount ?? 0;
      const success = summary?.successCount ?? 0;
      const errorCount =
        summary?.errorCount ??
        (Array.isArray(resp?.errors) ? resp.errors.length : 0);
      const firstError =
        Array.isArray(resp?.errors) && resp.errors.length
          ? resp.errors[0]
          : null;

      if (errorCount > 0) {
        const details = firstError?.error
          ? ` Row ${firstError?.row ?? "?"}: ${firstError.error}`
          : "";
        toast.error(
          `Import completed with errors. ${success}/${total} succeeded, ${errorCount} failed.${details}`
        );
      } else if (total || success) {
        toast.success(`Import completed. ${success}/${total} rows imported.`);
      } else {
        toast.success("File imported successfully");
      }
      try {
        // @ts-ignore available from hook
        setPage(1);
      } catch {}
      fetchCampaignData();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || err?.message || "Import failed"
      );
    } finally {
      setUploading(false);
      // reset input so same file can be re-selected
      if (e.target) e.target.value = "";
    }
  };

  console.log("campaignData ===>>>", campaignData);
  
  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  return (
    <div className="m-5 h-[80vh] overflow-auto">
      <ModernTable<CampaignRow>
        data={campaignData as CampaignRow[]}
        columns={columns}
        loading={loading}
        onSearch={setSearch}
        onPageChange={setPage}
        currentPage={page}
        totalPages={totalPage}
        totalItems={totalItems}
        title={`Campaigns - ${campaignName || ""}`}
        showSearch={true}
        showPagination={true}
        showAddButton={true}
        onAdd={handleOpenCreate}
        headerActions={
          <div className="flex items-center space-x-2">
            <div className="flex items-center gap-2">
              <select
                className="text-xs border rounded px-2 py-2 bg-white"
                value={leadStatusFilter}
                onChange={(e) => setLeadStatusFilter(e.target.value)}
              >
                <option value="">Lead Status</option>
                {LEAD_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <input
              id={fileInputId}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={handleCsvSelected}
            />
            <button
              type="button"
              onClick={handleUploadCsvClick}
              disabled={uploading}
              className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition-colors duration-200 text-sm disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              <span>{uploading ? "Importing..." : "Import CSV"}</span>
            </button>
          </div>
        }
      />

      <Modal
        isOpen={isCreateOpen}
        onClose={handleCloseCreate}
        style="max-w-xl w-full"
      >
        <h2 className="text-lg font-semibold mb-4">Create Campaign</h2>
        <form onSubmit={handleCreate} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs mb-1">Lead Name</label>
              <input
                name="leadName"
                value={form.leadName}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
                required
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Company Name</label>
              <input
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
                required
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Service</label>
              <select
                name="services"
                value={form.services}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              >
                <option value="">Select service</option>
                {services?.map((s: any) => (
                  <option
                    key={s.id || s._id || s.type}
                    value={s.id || s._id || ""}
                  >
                    {s.type || s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs mb-1">Company Website</label>
              <input
                name="companyWebsite"
                value={form.companyWebsite}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Personalization</label>
              <input
                name="personalization"
                value={form.personalization}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Follow 1</label>
              <input
                name="follow1"
                value={form.follow1}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Follow 2</label>
              <input
                name="follow2"
                value={form.follow2}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">AI Data</label>
              <input
                name="aiData"
                value={form.aiData}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Instagram</label>
              <input
                name="instagramLink"
                value={form.instagramLink}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Lead Web</label>
              <input
                name="leadWebLink"
                value={form.leadWebLink}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">LinkedIn Status</label>
              <select
                name="linkedInLeadStatus"
                value={form.linkedInLeadStatus}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              >
                <option value="">Select status</option>
                {LINKEDIN_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs mb-1">LinkedIn URL</label>
              <input
                name="linkedInLink"
                value={form.linkedInLink}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs mb-1">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                className="w-full border rounded px-2 py-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleCloseCreate}
              className="px-3 py-1 border rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {isSubmitting ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={isViewOpen}
        onClose={handleCloseView}
        style="max-w-2xl w-full p-4"
      >
        <h2 className="text-lg font-semibold mb-4">View Campaign</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500">Company Name</div>
            <div className="font-medium">{viewData?.companyName || "-"}</div>
          </div>
          <div className="">
            <div className="text-xs text-gray-500">Email</div>
            <div className="font-medium">{viewData?.email || "-"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Lead Name</div>
            <div className="font-medium">{viewData?.leadName || "-"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">LinkedIn Lead Status</div>
            <div className="font-medium">
              {viewData?.linkedInLeadStatus || "-"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Lead Status</div>
            <div className="font-medium">{viewData?.leadStatus || "-"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Service</div>
            <div className="font-medium">{viewData?.services || "-"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Instagram</div>
            <div className="font-medium break-all">
              {viewData?.instagramLink || "-"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Company Website</div>
            <a
              className="font-medium text-blue-600 break-all"
              href={viewData?.companyWebsite || "#"}
              target="_blank"
              rel="noreferrer"
            >
              {viewData?.companyWebsite || "-"}
            </a>
          </div>
          <div>
            <div className="text-xs text-gray-500">Lead Web Link</div>
            <a
              className="font-medium text-blue-600 break-all"
              href={viewData?.leadWebLink || "#"}
              target="_blank"
              rel="noreferrer"
            >
              {viewData?.leadWebLink || "-"}
            </a>
          </div>
          <div>
            <div className="text-xs text-gray-500">LinkedIn Link</div>
            <a
              className="font-medium text-blue-600 break-all"
              href={viewData?.linkedInLink || "#"}
              target="_blank"
              rel="noreferrer"
            >
              {viewData?.linkedInLink || "-"}
            </a>
          </div>
          <div className="col-span-2">
            <div className="text-xs text-gray-500">Description</div>
            <div className="font-medium whitespace-pre-wrap">
              {viewData?.description || "-"}
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-xs text-gray-500">Personalization</div>
            <div className="font-medium whitespace-pre-wrap">
              {viewData?.personalization || "-"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Follow 1</div>
            <div className="font-medium whitespace-pre-wrap">
              {viewData?.follow1 || "-"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Follow 2</div>
            <div className="font-medium whitespace-pre-wrap">
              {viewData?.follow2 || "-"}
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-xs text-gray-500">AI Data</div>
            <div className="font-medium whitespace-pre-wrap">
              {viewData?.aiData || "-"}
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-xs text-gray-500">Query</div>
            <div className="font-medium whitespace-pre-wrap">
              {viewData?.query || "-"}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={handleCloseView}
            className="px-3 py-1 border rounded"
          >
            Close
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={isEditOpen}
        onClose={handleCloseEdit}
        style="max-w-xl w-full"
      >
        <h2 className="text-lg font-semibold mb-4">Query</h2>
        <form onSubmit={handleUpdate} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs mb-1 ">Query</label>
              <textarea
                name="query"
                rows={5}
                value={form.query}
                onChange={handleChange}
                className="w-full border rounded px-2 py-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleCloseEdit}
              className="px-3 py-1 border rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {isSubmitting ? "Sending..." : "Send"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Campaign;
